import { PRODUCTS } from "./catalog";

export const MAX_COMPARE = 3;

const FAMILY_OF_CATEGORY = {
  "alluminio-porte-finestre": "finestre",
  "pvc-porte-finestre": "finestre",
  "alluminio-scorrevole": "scorrevoli",
  "pvc-scorrevole": "scorrevoli",
  "alluminio-facciate": "facciate",
  "alluminio-rivestimenti": "rivestimenti",
  "alluminio-parapetti": "parapetti",
  "alluminio-pareti-ufficio": "pareti",
  "pvc-oscuranti": "complementi",
};

export const FAMILY_LABELS = {
  finestre: { it: "Finestre e portefinestre", en: "Windows and French doors" },
  scorrevoli: { it: "Scorrevoli e grandi aperture", en: "Sliding and wide openings" },
  facciate: { it: "Facciate e vetrate", en: "Facades and curtain walls" },
  rivestimenti: { it: "Rivestimenti di facciata", en: "Facade cladding" },
  parapetti: { it: "Parapetti e vetrate per balconi", en: "Balustrades and balcony glazing" },
  pareti: { it: "Pareti divisorie per uffici", en: "Office partitions" },
  complementi: { it: "Complementi PVC", en: "PVC complements" },
};

export const familyOf = (product) =>
  product?.slug?.startsWith("asas-") ? FAMILY_OF_CATEGORY[product.category] || null : null;

const num = (s) => parseFloat(String(s).replace(",", "."));
const find = (features, re) => features.find((f) => re.test(f));
const nums = (s) => (String(s).match(/\d+(?:[.,]\d+)?/g) || []).map(num);

// Each row: key, label, extract(product) -> { text, value? }, better: "low" | "high" | undefined
const ROWS = [
  {
    key: "frame", it: "Profondità telaio / profilo", en: "Frame / profile depth",
    get: (p, f) => {
      const t = find(f, /^(Profondità|Telaio|Larghezza profilo|Larghezza in vista|Profilo \d)/i);
      return t ? { text: t.split(" — ")[0].replace(/^(Profondità (telaio|profilo|sistema)|Telaio|Larghezza (profilo|in vista)|Profilo)\s*/i, "") } : p.quickSpecs?.frame ? { text: `${p.quickSpecs.frame} mm` } : null;
    },
  },
  {
    key: "glass", it: "Vetro", en: "Glazing", better: "high",
    get: (p, f) => {
      const t = find(f, /^(Vetr|Glaz)/i);
      if (t) return { text: t.split(" — ")[0].replace(/^Vetr[oi]\s*/i, ""), value: Math.max(...nums(t.split(" — ")[0]).filter((n) => n < 200)) };
      return p.quickSpecs?.glass ? { text: `${p.quickSpecs.glass} mm`, value: p.quickSpecs.glass } : null;
    },
  },
  {
    key: "thermal", it: "Taglio termico", en: "Thermal break", better: "high",
    get: (p, f) => {
      const t = find(f, /(taglio termico|barretta|barra)/i);
      const m = t && t.match(/(\d+(?:[.,]\d+)?)\s*mm/);
      if (m) return { text: `${m[1]} mm`, value: num(m[1]) };
      return p.quickSpecs?.thermal ? { text: `${p.quickSpecs.thermal} mm`, value: p.quickSpecs.thermal } : null;
    },
  },
  {
    key: "uw", it: "Trasmittanza termica", en: "Thermal transmittance", better: "low",
    get: (p, f) => {
      const all = f.flatMap((x) => [...x.matchAll(/(U[cw]{1,2})[^\d]{0,12}(\d+[.,]\d+)/g)]);
      if (!all.length) return null;
      const best = all.reduce((a, b) => (num(b[2]) < num(a[2]) ? b : a));
      return { text: `${best[1]} ${best[2]} W/m²K`, value: num(best[2]) };
    },
  },
  {
    key: "acoustic", it: "Isolamento acustico", en: "Acoustic insulation", better: "high",
    get: (p, f) => {
      const t = find(f, /\d\s*dB/);
      const m = t && t.match(/(\d+(?:[.,]\d+)?)\s*dB/);
      return m ? { text: `${m[1]} dB`, value: num(m[1]) } : null;
    },
  },
  {
    key: "chambers", it: "Camere / guarnizioni", en: "Chambers / gaskets",
    get: (p, f) => {
      const t = [find(f, /camere/i), find(f, /guarnizion/i)].filter(Boolean);
      return t.length ? { text: t.map((x) => x.split(" — ")[0]).join(" · ") } : null;
    },
  },
  {
    key: "performance", it: "Aria, acqua e vento", en: "Air, water and wind",
    get: (p, f) => {
      const t = f.filter((x) => /(vento|aria|acqua|PAS24|urto|Carico)/i.test(x) && !/^U[cw]/.test(x));
      return t.length ? { text: t.join(" · ") } : null;
    },
  },
  {
    key: "openings", it: "Aperture e configurazioni", en: "Openings and configurations",
    get: (p, f) => {
      const t = find(f, /(battente|ribalta|vasistas|alzante|scorrevol|\blibro\b|binari|\bante\b|a pacchetto|apertur)/i);
      return t ? { text: t } : null;
    },
  },
  {
    key: "passive", it: "Passive House", en: "Passive House",
    get: (p) => (p.quickSpecs?.passive ? { text: "✓" } : null),
  },
];

export function compareTable(products, lang) {
  return ROWS.map((r) => {
    const cells = products.map((p) => r.get(p, p.it.features || []));
    if (!cells.some(Boolean)) return null;
    let bestIdx = [];
    const vals = cells.map((c) => (c && Number.isFinite(c.value) ? c.value : null));
    if (r.better && vals.filter((v) => v !== null).length > 1) {
      const target = r.better === "low" ? Math.min(...vals.filter((v) => v !== null)) : Math.max(...vals.filter((v) => v !== null));
      bestIdx = vals.map((v, i) => (v === target ? i : -1)).filter((i) => i >= 0);
      if (bestIdx.length === vals.filter((v) => v !== null).length) bestIdx = [];
    }
    return { key: r.key, label: r[lang] || r.it, cells, bestIdx };
  }).filter(Boolean);
}

export const productsBySlugs = (slugs) => slugs.map((s) => PRODUCTS[s]).filter(Boolean);
